import apiClient from './api';

class DeathCertificateService {
  /**
   * Get all death certificates with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.validation_status - Filter by: pending, valid, invalid
   * @param {string} params.search - Search by user email, name, or nominee name
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20, max: 100)
   * @returns {Promise} - Paginated list of death certificates
   */
  async getDeathCertificates(params = {}) {
    try {
      const response = await apiClient.get('/admin/death-certificates/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching death certificates:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get death certificate details by ID
   * @param {string|number} id - Certificate identifier
   * @returns {Promise} - Certificate details with presigned download URL
   */
  async getCertificateDetails(id) {
    try {
      const response = await apiClient.get(`/admin/death-certificates/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching certificate details:', error);
      return this.handleError(error);
    }
  }

  /**
   * Verify a death certificate
   * @param {string|number} id - Certificate identifier
   * @param {Object} data - Verification data
   * @param {string} data.validation_status - 'valid' or 'invalid'
   * @param {string} data.admin_remarks - Admin remarks
   * @param {File} data.supporting_doc_file - Optional supporting document (PDF, JPG, PNG)
   * @returns {Promise} - Verification confirmation
   */
  async verifyCertificate(id, data) {
    try {
      const formData = new FormData();
      formData.append('validation_status', data.validation_status);

      if (data.admin_remarks) {
        formData.append('admin_remarks', data.admin_remarks);
      }

      if (data.supporting_doc_file) {
        formData.append('supporting_doc_file', data.supporting_doc_file);
      }

      const response = await apiClient.post(`/admin/death-certificates/${id}/verify/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error verifying certificate:', error);
      return this.handleError(error);
    }
  }

  /**
   * Fetch certificate file as blob through authenticated API proxy and return blob URL
   * @param {string|number} certificateId - Certificate identifier
   * @returns {Promise} - Blob URL for the file
   */
  async getCertificateFileBlob(certificateId) {
    try {
      const response = await apiClient.get(`/admin/death-certificates/${certificateId}/download/`, {
        responseType: 'blob',
        params: { inline: 'true' },
      });
      const blobUrl = URL.createObjectURL(response.data);
      return {
        success: true,
        data: { blobUrl, mimeType: response.data.type },
      };
    } catch (error) {
      console.error('Error fetching certificate file:', error);
      return this.handleError(error);
    }
  }

  /**
   * Fetch admin supporting document as blob through authenticated API proxy
   * @param {string|number} certificateId - Certificate identifier
   * @returns {Promise} - Blob URL for the supporting document
   */
  async getAdminSupportingDocBlob(certificateId) {
    try {
      const response = await apiClient.get(`/admin/death-certificates/${certificateId}/download/`, {
        responseType: 'blob',
        params: { field: 'supporting_doc', inline: 'true' },
      });
      const blobUrl = URL.createObjectURL(response.data);
      return {
        success: true,
        data: { blobUrl, mimeType: response.data.type },
      };
    } catch (error) {
      console.error('Error fetching admin supporting doc:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        } else {
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          }
        }
      }

      if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'Certificate not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new DeathCertificateService();
